import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TierChip from './TierChip';
import { contributionAnnotation, describeValidation, tierOf, type ContributionTarget } from './contributions';
import { createAnnotation, getAnnotation, type Annotation, type DiagnoseDriver } from '../../apis';
import { stromaInset } from '../LandingAgent/askPresentation';

interface Props {
  target: ContributionTarget;
  /** The driver the note is added on, when it is. */
  driver?: DiagnoseDriver | null;
  displayDriverLabel?: string;
  /** Dimensions worth suggesting (the walk's attribution named them). */
  dimensions?: string[];
  onSaved: (saved: Annotation & Record<string, any>) => void;
  /** The dry run landed (the record re-read with its result). */
  onValidated?: (saved: Annotation & Record<string, any>) => void;
  onCancel: () => void;
}

const POLL_MS = 2000;
const POLL_FOR_MS = 60000;

/**
 * "Add what you know" (#193): a note in your own words, and — if you can
 * point at it — the slice it is about (a dimension and a value), so the
 * data can be asked. Saved as a signed, provisional contribution; the dry
 * run runs on the server and the box waits for its answer, then says it.
 */
export default function ContributeBox({ target, driver, displayDriverLabel, dimensions, onSaved, onValidated, onCancel }: Props) {
  const [note, setNote] = useState('');
  const [dimension, setDimension] = useState(dimensions?.[0] || '');
  const [segment, setSegment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<(Annotation & Record<string, any>) | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  /** When the server said a run is still under way: the moment it would allow a restart. */
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const timer = useRef<number | null>(null);
  const validation = saved?.provenance?.validation;
  const validating = validation?.status === 'validating';

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  // The retry control comes back when the server's own restart window opens.
  useEffect(() => {
    if (retryAt === null) return undefined;
    const id = window.setTimeout(() => setRetryAt(null), Math.max(0, retryAt - Date.now()));
    return () => window.clearTimeout(id);
  }, [retryAt]);

  // The dry run is asynchronous: the record is re-read until it lands (or a minute passes).
  useEffect(() => {
    if (!saved || !validating) return undefined;
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const fresh = await getAnnotation(saved.id);
        const v = (fresh as any).provenance?.validation;
        if (v?.status !== 'validating') { setSaved(fresh as any); onValidated?.(fresh as any); return; }
      } catch { /* keep waiting */ }
      if (Date.now() - startedAt < POLL_FOR_MS) timer.current = window.setTimeout(tick, POLL_MS);
      else setTimedOut(true);   // still validating after a minute: the run may be stuck — offer to run it again
    };
    timer.current = window.setTimeout(tick, POLL_MS);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [saved, validating, onValidated]);

  const save = async () => {
    if (!note.trim() || saving) return;
    if ((dimension.trim() && !segment.trim()) || (!dimension.trim() && segment.trim())) {
      setError('A slice needs both a dimension and a value — or leave both empty');
      return;
    }
    setSaving(true); setError(null);
    try {
      const row = await createAnnotation(contributionAnnotation(target, { note, dimension, segment, driver }));
      setSaved(row as any);
      onSaved(row as any);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not save what you know');
    } finally {
      setSaving(false);
    }
  };

  const retry = async () => {
    if (!saved || retrying) return;
    setRetrying(true);
    try {
      const { validateContribution } = await import('../../apis');
      const { row, started, retryAfterS } = await validateContribution(saved.id);
      const stillRunning = !started && (row as any)?.provenance?.validation?.status === 'validating' && retryAfterS > 0;
      // Not started because a run is under way: the server says when it would restart it — say so, and keep waiting.
      setRetryAt(stillRunning ? Date.now() + retryAfterS * 1000 : null);
      setTimedOut(stillRunning);
      setSaved(row as any);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not run the dry run again');
    } finally {
      setRetrying(false);
    }
  };

  if (saved) {
    const tier = tierOf(validation);
    return (
      <Box data-testid="contribute-result" sx={{ p: 1.25, ...stromaInset, borderColor: 'divider', display: 'grid', gap: 0.5 }}>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Saved{validating ? ' — validating against the data…' : ''}</Typography>
          <TierChip tier={tier} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }} aria-live="polite" data-testid="contribute-status">
            {validating ? 'a dry run is checking the slice you named' : describeValidation(validation)}
          </Typography>
        </Box>
        {((!validating && (validation?.outcome === 'error' || validation?.outcome === 'no_effect')) || (validating && timedOut)) && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {validating && retryAt !== null ? 'A dry run is still under way on the server — it can be run again once it is given up on.'
              : validating ? 'The dry run has not come back yet — it may be queued, or it may be stuck.'
                : validation?.outcome === 'no_effect' ? 'It stays provisional — signed and visible, but the data did not back it on this window.' : 'The dry run could not be completed.'}
            {' '}<Button size="small" variant="text" disabled={retrying || retryAt !== null} onClick={retry} sx={{ py: 0, minHeight: 0, fontSize: '0.72rem' }} data-testid="contribute-retry">
              {retrying ? 'Running…' : retryAt !== null ? `Run it again in ${Math.max(1, Math.ceil((retryAt - Date.now()) / 60000))} min` : 'Run it again'}
            </Button>
          </Typography>
        )}
        {!validating && validation?.outcome === 'untestable' && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Saved as what you know — without a slice to test it stays provisional.</Typography>
        )}
        {error && <Typography variant="caption" sx={{ color: 'error.main' }}>{error}</Typography>}
        <Box><Button size="small" variant="text" onClick={onCancel} data-testid="contribute-close">Close</Button></Box>
      </Box>
    );
  }

  return (
    <Box data-testid="contribute-box" sx={{ p: 1.25, ...stromaInset, borderColor: 'divider', display: 'grid', gap: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
        Add what you know{driver ? ` about ${displayDriverLabel || driver.label || driver.title || driver.id}` : ''}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Saved with your name, provisional until a dry run against the data backs it. Point at the slice — a dimension and a value — and it will be checked over this window.
      </Typography>
      <TextField size="small" multiline minRows={2} label="What you know" value={note} onChange={(e) => setNote(e.target.value.slice(0, 2000))}
        inputProps={{ 'data-testid': 'contribute-note', maxLength: 2000 }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField size="small" label="Dimension" value={dimension} onChange={(e) => setDimension(e.target.value.slice(0, 200))}
          inputProps={{ 'data-testid': 'contribute-dimension', list: 'contribute-dimensions' }} sx={{ minWidth: 180 }} />
        {dimensions && dimensions.length > 0 && (
          <datalist id="contribute-dimensions">{dimensions.map((d) => <option key={d} value={d} />)}</datalist>
        )}
        <TextField size="small" label="Value" value={segment} onChange={(e) => setSegment(e.target.value.slice(0, 200))}
          inputProps={{ 'data-testid': 'contribute-segment' }} sx={{ minWidth: 160 }} />
      </Box>
      {error && <Typography variant="caption" sx={{ color: 'error.main' }} data-testid="contribute-error">{error}</Typography>}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" disabled={!note.trim() || saving} onClick={save} data-testid="contribute-save">{saving ? 'Saving…' : 'Save'}</Button>
        <Button size="small" variant="text" onClick={onCancel}>Cancel</Button>
      </Box>
    </Box>
  );
}
